import { TokenIcon } from "@/components/brand/TokenIcon";
import { Button } from "@/components/ui/Button";
import { useBadgeProgress } from "@/hooks/useBadgeProgress";
import {
	useSavingsBalance,
	useStakingBalance,
} from "@/hooks/useGovernance";
import { useUserVaults } from "@/hooks/useUserVaults";
import { useAuth } from "@/lib/auth";
import { PLATFORM_NAME, ROUTES } from "@/lib/routes";
import {
	getSessionTtlHours,
	SESSION_TTL_OPTIONS,
	setSessionTtlHours,
	sessionTtlLabel,
} from "@/lib/session-prefs";
import { copyToClipboard } from "@/lib/clipboard";
import { formatNum, shortPrincipal } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { Copy } from "lucide-react";
import { Link } from "react-router-dom";
import { useCallback, useState } from "react";
import { toast } from "sonner";

export default function ProfilePage() {
	const {
		isAuthenticated,
		principal,
		isLoggingIn,
		login,
		logout,
		configError,
		sessionStatus,
		reconnectProtocol,
		reportSessionIssue,
	} = useAuth();
	const { badges, level, total, next } = useBadgeProgress();
	const progressPct = total > 0 ? Math.round((level / total) * 100) : 0;
	const { data: savingsBal = 0 } = useSavingsBalance();
	const { data: staked = 0 } = useStakingBalance();
	const { data: vaults = [] } = useUserVaults();
	const [ttlHours, setTtlHours] = useState(() => getSessionTtlHours());

	const copyPrincipal = useCallback(async () => {
		if (!principal) {
			toast.error("Connect to reveal your principal first");
			return;
		}
		const ok = await copyToClipboard(principal);
		if (ok) toast.success("Principal copied");
		else toast.error("Copy failed — select the principal and copy manually");
	}, [principal]);

	function saveTtl(hours: number) {
		setSessionTtlHours(hours);
		setTtlHours(hours);
		toast.success(
			`Session length set to ${SESSION_TTL_OPTIONS.find((o) => o.hours === hours)?.label ?? hours + "h"} — applies on next login`,
		);
	}

	return (
		<div className="container mx-auto max-w-2xl space-y-6 px-4 py-8 pb-16">
			<p className="section-eyebrow">Profile</p>
			<h1 className="text-xl font-bold sm:text-2xl">Your account</h1>
			<p className="text-sm text-muted-foreground">
				{PLATFORM_NAME} session via Internet Identity. Manage access length and
				learning-path badges.
			</p>

			<div className="card-surface space-y-4 p-5">
				{isAuthenticated && principal ? (
					<>
						<div>
							<p className="text-[10px] uppercase text-muted-foreground">
								Principal
							</p>
							<div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-start">
								<p className="min-w-0 flex-1 break-all rounded-xl bg-muted px-3 py-2.5 font-mono text-sm">
									{principal}
								</p>
								<button
									type="button"
									className="inline-flex min-h-10 shrink-0 items-center justify-center gap-1.5 rounded-xl border border-border px-3 text-xs font-bold"
									onClick={() => void copyPrincipal()}
								>
									<Copy className="size-3.5" aria-hidden />
									Copy
								</button>
							</div>
							<p className="mt-1 text-xs text-muted-foreground">
								{shortPrincipal(principal)}
							</p>
						</div>
						<div className="flex flex-wrap gap-2 text-[11px]">
							<span className="rounded-full border border-border px-2.5 py-1 font-semibold capitalize text-muted-foreground">
								Session · {sessionStatus.replace("_", " ")}
							</span>
							<span className="rounded-full border border-border px-2.5 py-1 font-semibold text-muted-foreground">
								TTL · {sessionTtlLabel()}
							</span>
						</div>
						<div className="flex flex-wrap gap-2">
							<Button variant="secondary" onClick={() => void logout()}>
								Log out
							</Button>
							<Button
								variant="outline"
								onClick={() => void reconnectProtocol()}
							>
								Reconnect protocol
							</Button>
							<Link to={ROUTES.wallet}>
								<Button variant="outline">Open wallet</Button>
							</Link>
						</div>
						{(sessionStatus === "degraded" ||
							sessionStatus === "degraded_prompt" ||
							sessionStatus === "expired") && (
							<button
								type="button"
								className="text-left text-xs font-semibold text-warning"
								onClick={() => reportSessionIssue("degraded")}
							>
								Vaults missing? Fix session →
							</button>
						)}
					</>
				) : (
					<>
						<p className="text-sm text-muted-foreground">
							Not connected. Connect to mint, repay, and manage vaults.
						</p>
						<Button
							variant="primary"
							className="!bg-primary"
							loading={isLoggingIn}
							onClick={() => void login()}
						>
							Connect
						</Button>
					</>
				)}
				{configError ? (
					<p className="text-xs text-warning">{configError}</p>
				) : null}
			</div>

			<section className="card-surface space-y-4 p-5">
				<div>
					<p className="section-eyebrow">Session length</p>
					<h2 className="text-lg font-bold">Keep me signed in</h2>
					<p className="mt-1 text-sm text-muted-foreground">
						Internet Identity max session duration. Applied the next time you
						log in. II may still end earlier on some devices.
					</p>
				</div>
				<ul className="space-y-2">
					{SESSION_TTL_OPTIONS.map((opt) => (
						<li key={opt.hours}>
							<button
								type="button"
								onClick={() => saveTtl(opt.hours)}
								className={cn(
									"flex w-full items-start gap-3 rounded-xl border px-3 py-3 text-left transition",
									ttlHours === opt.hours
										? "border-primary bg-primary/10"
										: "border-border hover:border-primary/40",
								)}
							>
								<span
									className={cn(
										"mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border",
										ttlHours === opt.hours
											? "border-primary bg-primary"
											: "border-border",
									)}
								>
									{ttlHours === opt.hours ? (
										<span className="size-1.5 rounded-full bg-primary-foreground" />
									) : null}
								</span>
								<span>
									<span className="block text-sm font-bold">{opt.label}</span>
									<span className="text-xs text-muted-foreground">
										{opt.description}
									</span>
								</span>
							</button>
						</li>
					))}
				</ul>
			</section>

			{isAuthenticated ? (
				<section className="card-surface space-y-3 p-5">
					<p className="section-eyebrow">Your protocol balances</p>
					<ul className="grid gap-2 sm:grid-cols-3">
						<li className="rounded-xl border border-border p-3">
							<div className="flex items-center gap-1.5">
								<TokenIcon token="USDM" size="sm" />
								<p className="text-[10px] uppercase text-muted-foreground">
									Savings
								</p>
							</div>
							<p className="mt-1 font-mono-amount text-base font-bold">
								{formatNum(savingsBal, 4)} USDM
							</p>
						</li>
						<li className="rounded-xl border border-border p-3">
							<div className="flex items-center gap-1.5">
								<TokenIcon token="MMK" size="sm" />
								<p className="text-[10px] uppercase text-muted-foreground">
									Staked MMK
								</p>
							</div>
							<p className="mt-1 font-mono-amount text-base font-bold">
								{formatNum(staked, 2)}
							</p>
						</li>
						<li className="rounded-xl border border-border p-3">
							<p className="text-[10px] uppercase text-muted-foreground">
								Your vaults
							</p>
							<p className="mt-1 font-mono-amount text-base font-bold">
								{vaults.length}
							</p>
						</li>
					</ul>
				</section>
			) : null}

			<section className="card-surface space-y-4 p-5">
				<div className="flex items-end justify-between gap-2">
					<div>
						<p className="section-eyebrow">Gamification</p>
						<p className="text-lg font-bold">
							{level} / {total} badges
						</p>
						<p className="text-xs text-muted-foreground">
							Tutorial path only — no token rewards.
						</p>
					</div>
					<span className="font-mono text-sm text-primary">{progressPct}%</span>
				</div>
				<div className="h-2 overflow-hidden rounded-full bg-muted">
					<div
						className="h-full rounded-full bg-primary transition-all"
						style={{ width: `${progressPct}%` }}
					/>
				</div>
				{next ? (
					<div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
						<p className="text-[10px] font-semibold uppercase text-primary">
							Next mission
						</p>
						<p className="mt-1 text-sm font-bold">{next.title}</p>
						<p className="mt-0.5 text-xs text-muted-foreground">
							{next.howTo}
						</p>
						<Link
							to={next.to}
							className="mt-2 inline-flex text-xs font-bold text-primary"
						>
							Go →
						</Link>
					</div>
				) : (
					<p className="text-xs text-muted-foreground">
						All badges earned — path complete.
					</p>
				)}
				<ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
					{badges.map((b) => (
						<li
							key={b.id}
							className={cn(
								"rounded-xl border px-3 py-3",
								b.state === "earned"
									? "border-primary/40 bg-primary/10"
									: b.state === "available"
										? "border-amber-500/40 bg-amber-500/10"
										: "border-border opacity-80",
							)}
						>
							<div className="flex items-start justify-between gap-2">
								<p className="text-sm font-bold">{b.title}</p>
								<span className="text-[10px] font-semibold uppercase text-muted-foreground">
									{b.state}
								</span>
							</div>
							<p className="mt-1 text-[11px] text-muted-foreground">
								{b.description}
							</p>
							{b.state !== "earned" ? (
								<p className="mt-1 text-[11px] font-medium text-foreground/80">
									{b.howTo}
								</p>
							) : null}
							{b.state !== "earned" ? (
								<Link
									to={b.to}
									className="mt-2 inline-block text-[11px] font-bold text-primary"
								>
									Open →
								</Link>
							) : null}
						</li>
					))}
				</ul>
			</section>
		</div>
	);
}
